import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";

import { CHART_CAPTURE_PREVIEW_URL } from "./constants";
import { getHeaders } from "./utils";
import "./chart-capture-modal.css";

const SYMBOLS = ["SPY", "QQQ"];

/** Today in New York, which is the day the capture scheduler thinks in. */
function today() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

/**
 * Runs the nightly chart capture on demand and shows what it produced.
 *
 * The same render over the same window the 22:00 scheduler uses, with the R2
 * upload taken off the end -- so a layout that has drifted, lost a study or come
 * up behind a promo can be caught before the night's charts are filed rather
 * than the next morning by looking at what was filed.
 *
 * Nothing is written and nothing is skipped: a day whose charts are already on
 * R2 renders again here, because the question being asked is what the layout
 * draws now, not what is stored.
 */
export default function ChartCaptureModal({ show, onHide }) {
  const [symbol, setSymbol] = useState(SYMBOLS[0]);
  const [date, setDate] = useState(today);
  const [preview, setPreview] = useState(null);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    if (show) {
      setPreview(null);
      setDate(today());
    }
  }, [show]);

  const renderPreview = async () => {
    setRendering(true);
    try {
      // A full render: a cold Chromium launch, the layout load, the pane fit and
      // the range apply, then the screenshot. Minutes, not seconds.
      const response = await axios({
        method: "get",
        url: CHART_CAPTURE_PREVIEW_URL,
        params: { symbol, date, format: "json" },
        headers: getHeaders(),
        withCredentials: true,
        timeout: 300000,
      });
      setPreview(response.data);
    } catch (error) {
      alert("Failed to capture the layout.\n\n" + describe(error));
    } finally {
      setRendering(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered data-bs-theme="dark">
      <Modal.Header closeButton className="bg-dark text-light border-secondary">
        <Modal.Title>Chart Capture</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light chart-capture-body">
        <div className="chart-capture-toolbar">
          <Form.Select
            size="sm"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            style={{ width: 110 }}
            disabled={rendering}
          >
            {SYMBOLS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            size="sm"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ width: 170 }}
            disabled={rendering}
          />

          <Button size="sm" variant="primary" onClick={renderPreview} disabled={rendering}>
            {rendering ? <Spinner as="span" size="sm" /> : "Preview"}
          </Button>

          {preview && (
            <span className="chart-capture-status">
              {preview.symbol} · {preview.from.slice(11, 16)}–{preview.to.slice(11, 16)} ET ·{" "}
              {Math.round(preview.bytes / 1024)} KB
            </span>
          )}
        </div>

        {preview ? (
          <div className="chart-capture-image">
            <img src={preview.image} alt={`${preview.symbol} chart for ${preview.date}`} />
          </div>
        ) : (
          <div className="chart-capture-empty">
            {rendering
              ? "Rendering… the layout has to load, size its panes and apply the window. This takes a while."
              : "Press Preview to run the nightly capture for this day, without uploading it."}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function describe(error) {
  const details = error.response && error.response.data;
  if (details && details.error) {
    return details.error;
  }
  return error.message || "No error details provided.";
}
